import { useEffect, useState } from 'react';
import { Close, Call, ContactPhone } from '@mui/icons-material';
import { Drawer, Typography, Box, IconButton, Divider, Tabs, Tab } from '@mui/material';

import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { countries } from '@frontend/features/common/constants/countries';
import { useGetAgencyById } from '@frontend/features/agencies/queries';
import { useGetCaseById } from '@frontend/features/cases/queries';
import { PhoneDialer } from './PhoneDialer';
import { modalStyles } from './styles';
import { OutgoingCallModalProps } from './types';
import { PhoneDialerClientInfo } from './PhoneDialerClientInfo';
import { useTranslation } from 'react-i18next';

const OutgoingCallModal = ({ open, onClose, clientData }: OutgoingCallModalProps) => {
  const { handleCall: twilioHandleCall, caseId } = useTwilio();
  const [tabValue, setTabValue] = useState(Boolean(clientData?.clientPhoneCountryCode && clientData?.clientPhoneNumber) ? 1 : 0);
  const [dialNumber, setDialNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('CO');
  const { t } = useTranslation();

  const { data: caseData } = useGetCaseById({ id: caseId });
  const { data: agencyData } = useGetAgencyById({ id: caseData?.agencyId });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleKeyPress = (key: string) => {
    setDialNumber((prev) => prev + key);
  };

  const handleClearNumber = () => {
    setDialNumber('');
  };

  const handleCountryChange = (event: any) => {
    setSelectedCountry(event.target.value);
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDialNumber(event.target.value);
  };

  const handleCall = (useClientData: boolean) => {
    let phoneNumber = '';

    if (useClientData && clientData?.clientPhoneCountryCode && clientData?.clientPhoneNumber) {
      phoneNumber = `+${clientData.clientPhoneCountryCode}${clientData.clientPhoneNumber}`;
    } else {
      const { phoneCode } = countries.find((country) => country.code === selectedCountry) || { phoneCode: '' };
      phoneNumber = `+${phoneCode}${dialNumber}`;
    }
    
    twilioHandleCall(phoneNumber);
  };

  useEffect(() => {
    if (agencyData) {
      const country = countries.find((country) => country.name === agencyData.country);
      if (country) {
        setSelectedCountry(country.code);
      }
    }
  }, [agencyData]);

  const contactInfoAvailable = Boolean(clientData?.clientPhoneCountryCode && clientData?.clientPhoneNumber);


  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: modalStyles.drawer }}>
      <Box sx={modalStyles.container}>
        <Box sx={modalStyles.header}>
          <Typography variant="h6">Teléfono</Typography>
          <IconButton size="small" onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Divider />

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<Call />} sx={modalStyles.tab} />
          <Tab icon={<ContactPhone />} sx={modalStyles.tab} disabled={!contactInfoAvailable} title={ !contactInfoAvailable ? t('cases.view.content.missingContactData') : ''}/>
        </Tabs>

        <Divider />

        {tabValue === 0 && (
          <Box sx={modalStyles.content}>
            <PhoneDialer
              selectedCountry={selectedCountry}
              dialNumber={dialNumber}
              onCountryChange={handleCountryChange}
              onNumberChange={handleNumberChange}
              onClearNumber={handleClearNumber}
              onKeyPress={handleKeyPress}
              onCall={() => handleCall(false)}
            />
          </Box>
        )}

        {(tabValue === 1 && clientData) && (
          <Box sx={{
            ...modalStyles.content,
            boxSizing: 'border-box',
          }}>
            <PhoneDialerClientInfo clientData={clientData} onCall={() => handleCall(true)} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default OutgoingCallModal;
